import React, { useEffect, useState } from 'react';
import { Row, Col, Form, Button, Table, Alert, Container } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';

import {
  useGetOpeningStockDetailsQuery,
  useUpdateOpeningStockMutation,
} from '../slices/openingStocksApiSlice';

import { useGetProductsQuery } from '../slices/productsApiSlice';

import Loader from '../components/Loader';
import Message from '../components/Message';
import { toast } from 'react-toastify';

const OpeningStockEditScreen = () => {
  const { id: openingStockId } = useParams();
  const navigate = useNavigate();

  const { data: openingStock, isLoading, error } =
    useGetOpeningStockDetailsQuery(openingStockId);

  const {
    data: productsData = { products: [] },
    isLoading: loadingProducts,
    error: errorProducts,
  } = useGetProductsQuery({ pageNumber: 1 });

  const [updateOpeningStock] = useUpdateOpeningStockMutation();

  const [items, setItems] = useState([]);
  const [note, setNote] = useState('');
  const [supplierDetails, setSupplierDetails] = useState('');
  const [date, setDate] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [status, setStatus] = useState('Completed');

  // ------------------------------------------------------------
  // LOAD OPENING STOCK INTO STATE
  // ------------------------------------------------------------
  useEffect(() => {
    if (openingStock) {
      setItems(openingStock.openingStockItems);
      setNote(openingStock.note);
      setSupplierDetails(openingStock.supplierDetails);
      setDate(openingStock.date?.substring(0, 10));
      setExpiryDate(openingStock.expiryDate?.substring(0, 10));
      setStatus(openingStock.status);
    }
  }, [openingStock]);

  // ------------------------------------------------------------
  // UPDATE ITEM FIELD
  // ------------------------------------------------------------
  const updateItemField = (index, field, value) => {
    setItems((prev) => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        [field]: value,
      };
      return updated;
    });
  };

  // ------------------------------------------------------------
  // CHANGE PRODUCT (Mode 5 replacement)
  // ------------------------------------------------------------
  const changeProduct = (index, productId) => {
    const product = productsData.products.find((p) => p._id === productId);
    if (!product) return;

    setItems((prev) => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        product: product._id,
        name: product.name,
        price: product.price,
        images: Array.isArray(product.images) ? product.images : [],
      };
      return updated;
    });
  };

  // ------------------------------------------------------------
  // SAVE CHANGES
  // ------------------------------------------------------------
  const handleSave = async () => {
    try {
      const payload = {
        openingStockId,
        openingStockItems: items.map((item) => ({
          product: item.product,
          name: item.name,
          price: item.price,
          qty: item.qty,
          images: Array.isArray(item.images) ? item.images : [],
          itemCost: item.itemCost || 0,
        })),
        supplierDetails,
        note,
        date,
        expiryDate,
        status,
      };

      await updateOpeningStock(payload).unwrap();

      toast.success('Opening Stock updated successfully');
      navigate(`/openingstock/${openingStockId}`);
    } catch (err) {
      toast.error(err?.data?.message || 'Update failed');
    }
  };

  // ------------------------------------------------------------
  // UI
  // ------------------------------------------------------------
  return (
    <Container>
      <Row className="mb-3">
        <Col>
          <h1>Edit Opening Stock</h1>
        </Col>
      </Row>

      {isLoading ? (
        <Loader />
      ) : error ? (
        <Message variant="danger">
          {error?.data?.message || 'Failed to load opening stock'}
        </Message>
      ) : (
        <>
          <Row className="mb-3">
            <Col md={4}>
              <Form.Group>
                <Form.Label>Date</Form.Label>
                <Form.Control
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                />
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group>
                <Form.Label>Expiry Date</Form.Label>
                <Form.Control
                  type="date"
                  value={expiryDate}
                  onChange={(e) => setExpiryDate(e.target.value)}
                />
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group>
                <Form.Label>Status</Form.Label>
                <Form.Select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                >
                  <option value="Pending">Pending</option>
                  <option value="Completed">Completed</option>
                  <option value="Cancelled">Cancelled</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col>
              <Form.Group>
                <Form.Label>Note</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                />
              </Form.Group>
            </Col>
          </Row>

          <h4>Items</h4>

          <Table striped bordered hover responsive className="table-sm">
            <thead>
              <tr>
                <th>Product</th>
                <th>Name</th>
                <th>Qty</th>
                <th>Price</th>
                <th>Images</th>
              </tr>
            </thead>

            <tbody>
              {items.map((item, index) => (
                <tr key={index}>
                  <td>
                    <Form.Select
                      value={item.product}
                      onChange={(e) => changeProduct(index, e.target.value)}
                    >
                      <option value="">Select Product</option>
                      {productsData.products.map((p) => (
                        <option key={p._id} value={p._id}>
                          {p.name}
                        </option>
                      ))}
                    </Form.Select>
                  </td>

                  <td>
                    <Form.Control
                      type="text"
                      value={item.name}
                      onChange={(e) =>
                        updateItemField(index, 'name', e.target.value)
                      }
                    />
                  </td>

                  <td>
                    <Form.Control
                      type="number"
                      value={item.qty}
                      onChange={(e) =>
                        updateItemField(index, 'qty', Number(e.target.value))
                      }
                    />
                  </td>

                  <td>
                    <Form.Control
                      type="number"
                      value={item.price}
                      onChange={(e) =>
                        updateItemField(index, 'price', Number(e.target.value))
                      }
                    />
                  </td>

                  <td>
                    {item.images?.length > 0 ? (
                      item.images.map((img, idx) => (
                        <div key={idx}>{img}</div>
                      ))
                    ) : (
                      <span>No images</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>

          <Button variant="primary" onClick={handleSave}>
            Save Changes
          </Button>
        </>
      )}
    </Container>
  );
};

export default OpeningStockEditScreen;
